import React, { FunctionComponent, useCallback, useState } from 'react';
import { ChevronRightIcon, collapseClasses, makeStyles, treeItemClasses, TreeView } from '@rbx/ui';

const useNavigationTreeStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,

    [`& .${treeItemClasses.focused}`]: {
      backgroundColor: 'unset',
    },

    [`& .${treeItemClasses.group}`]: {
      paddingTop: 4,
      marginLeft: 0,

      [`& .${treeItemClasses.group}`]: {
        marginLeft: 24,
      },

      [`& .${treeItemClasses.content}`]: {
        paddingLeft: 24,
        [`& .${treeItemClasses.label}`]: {
          padding: 2,
        },
      },
    },

    [`& .${treeItemClasses.content}`]: {
      minHeight: 40,
      padding: 0,
      flexDirection: 'row-reverse',
      [`& .${treeItemClasses.label}`]: {
        padding: 0,
      },
      [`&:hover .${treeItemClasses.label}`]: {
        color: theme.palette.content.standard,
      },
      [`&.${treeItemClasses.selected} .${treeItemClasses.label}`]: {
        color: theme.palette.content.standard,
        fontWeight: theme.typography.fontWeightBold,
      },
    },

    [`& .${treeItemClasses.label}`]: {
      padding: 0,
      color: theme.palette.content.standard,
      fontSize: theme.typography.fontSize,
    },

    [`& .${treeItemClasses.iconContainer}:empty`]: {
      width: 0,
    },

    [`& .${collapseClasses.wrapperInner}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
  },
  collapseIcon: {
    transform: 'rotate(-90deg)',
  },
  expandIcon: {
    transform: 'rotate(90deg)',
  },
}));

export type TNavigationTreeProps = {
  defaultSelected?: string;
  defaultExpanded?: string[];
  selected?: string;
  onNodeSelect?: (event: React.SyntheticEvent, nodeIds: string) => void;
  onExpanded?: (nodeId: string) => void;
  onCollapsed?: (nodeId: string) => void;
};

const NavigationTree: FunctionComponent<React.PropsWithChildren<TNavigationTreeProps>> = ({
  children,
  onExpanded,
  onCollapsed,
  defaultExpanded,
  ...treeProps
}) => {
  const {
    classes: { root, expandIcon, collapseIcon },
  } = useNavigationTreeStyles();
  const [expanded, setExpanded] = useState(defaultExpanded ?? []);

  const onNodeToggle = useCallback(
    (_: React.SyntheticEvent, nodeIds: string[]) => {
      if (onExpanded) {
        nodeIds.filter((nodeId) => !expanded.includes(nodeId)).forEach(onExpanded);
      }
      if (onCollapsed) {
        expanded.filter((nodeId) => !nodeIds.includes(nodeId)).forEach(onCollapsed);
      }
      setExpanded(nodeIds);
    },
    [expanded, onCollapsed, onExpanded],
  );

  return (
    <TreeView
      variant='default'
      defaultExpandIcon={<ChevronRightIcon classes={{ root: expandIcon }} />}
      defaultCollapseIcon={<ChevronRightIcon classes={{ root: collapseIcon }} />}
      classes={{ root }}
      expanded={expanded}
      onNodeToggle={onNodeToggle}
      {...treeProps}>
      {children}
    </TreeView>
  );
};

export default NavigationTree;
